import {
  Accordion,
  ActionIcon,
  AppShell,
  Badge,
  Box,
  Burger,
  Button,
  Flex,
  Group,
  Image,
  rem,
  Stack,
  UnstyledButton,
} from '@mantine/core';
import { hasAdminDevSectionItems, hasFinanceSectionItems, hasPatientAccessSectionItems } from './navSections';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { Practitioner } from '@medplum/fhirtypes';
import { ErrorBoundary, Loading, useMedplum, useMedplumProfile } from '@medplum/react';
import {
  IconArchive,
  IconBell,
  IconBuildings,
  IconCalendar,
  IconCalendarEvent,
  IconChartBar,
  IconFileSpreadsheet,
  IconChevronDown,
  IconCoin,
  IconDialpad,
  IconHeartbeat,
  IconInbox,
  IconLayoutGrid,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconListCheck,
  IconMessage,
  IconPhone,
  IconPrinter,
  IconStethoscope,
  IconTestPipe,
  IconUsers,
} from '@tabler/icons-react';
import { useFeatureFlagEnabled, usePostHog } from 'posthog-js/react';
import { ReactElement, Suspense, useEffect, useState } from 'react';
import { Link, Navigate, NavLink, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router';
import classes from './App.module.css';
import everselfIcon from './assets/everself_icon.svg';
import everselfLogo from './assets/everself_logo.svg';
import { GlobalCallNotification } from './components/GlobalCallNotification';
import { GlobalPhoneControls } from './components/GlobalPhoneControls';
import { GlobalSearch } from './components/GlobalSearch';
import { PushNotificationsPrompt } from './components/NotificationsPrompt';
import { CurrentUserProvider } from './contexts/CurrentUserProvider';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsProvider';
import { TwilioPhoneProvider } from './contexts/TwilioPhoneProvider';
import { useNotificationAnalytics } from './hooks/useNotificationAnalytics';
import { useNotificationsCount } from './hooks/useNotificationsCount';
import { ArchivePage } from './pages/ArchivePage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { InboxPage } from './pages/InboxPage';
import { PartnersPage } from './pages/PartnersPage/PartnersPage';
import { PatientListPage } from './pages/PatientListPage/PatientListPage';
import { PatientTimelinePage } from './pages/PatientTimelinePage';
import { PhoneCallsPage } from './pages/PhoneCallsPage';
import { TaskLogPage } from './pages/TaskLogPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { SetPasswordPage } from './pages/SetPasswordPage';
import { SignInPage } from './pages/SignInPage';
import { SimulatePatientChatPage } from './pages/SimulatePatientChatPage';
import { ImportHealthiePatientsPage } from './pages/ImportHealthiePatientsPage';
import { UpdatePatientStatesPage } from './pages/UpdatePatientStatesPage';
import { SmsMessagesPage } from './pages/SmsMessagesPage';
import { FaxingPage } from './pages/FaxingPage/FaxingPage';
import { ensurePushSubscription } from './services/push';
import { UserMenu } from './components/UserMenu/UserMenu';
import { handleCallActionEvent } from './services/notification-handlers';
import { trackSidebarToggled } from './services/analytics';
import { isProvider } from '@bariendo/lib/profile';
import { useCurrentUser } from './hooks/useCurrentUser';
import { FEATURE_FLAGS } from '@bariendo/lib/constants';
import { PartnerTimelinePage } from './pages/PartnersPage/PartnerTimeline/PartnerTimelinePage';
import { SchedulingPage } from './pages/SchedulingPage/SchedulingPage';
import { PreProcedurePage } from './pages/PatientCentral/PreProcedurePage';
import { PostProcedurePage } from './pages/PatientCentral/PostProcedurePage';
import { ProcedureSchedulingPage } from './pages/PatientCentral/ProcedureSchedulingPage';
import { PacManagerDashboardPage } from './pages/PacManagerDashboardPage';
import { IVRConfigPage } from './pages/IVRConfigPage';
import { IVRTemplateEditorPage } from './pages/IVRConfigPage/IVRTemplateEditorPage';
import { OnCallSchedulePage } from './pages/OnCallSchedulePage';
import { PatientExportPage } from './pages/PatientExportPage/PatientExportPage';
import { FinanceOpsPage } from './pages/FinanceOpsPage/FinanceOpsPage';
import { ProvidersPage } from './pages/ProvidersPage';

// Constants for better maintainability
const FIRST_NESTED_ICON_MARGIN = 32;

const ICON_SIZES = {
  small: 14,
  medium: 16,
  large: 20,
} as const;

const NAVBAR_WIDTH = {
  collapsed: 70,
  expanded: 250,
} as const;

type FeatureFlagsConfig = {
  canAccessPartners?: boolean;
};

// Navigation items configuration
const NAV_ITEMS = ({ canAccessPartners }: FeatureFlagsConfig) =>
  [
    {
      icon: <IconUsers size={ICON_SIZES.medium} />,
      label: 'Patients',
      to: '/Patient',
    },
    canAccessPartners
      ? {
          icon: <IconBuildings size={ICON_SIZES.medium} />,
          label: 'Partners',
          to: '/Organizations',
        }
      : undefined,
    {
      icon: <IconPrinter size={ICON_SIZES.medium} />,
      label: 'Faxing',
      to: '/Faxing',
    },
  ] as const;

function PublicOnlyRoute() {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;

  // Redirect authenticated providers to home page
  if (medplum.isAuthenticated() && isProvider(profile)) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}

function ProtectedRoute() {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const location = useLocation();

  if (!medplum.isAuthenticated()) {
    return <Navigate to="/signin" state={{ from: location.pathname }} replace />;
  }

  // Block access if user is not a provider (e.g., Patient)
  if (!isProvider(profile)) {
    medplum.signOut().catch(() => {
      // Sign out failed, but redirect anyway
    });
    return <Navigate to="/signin" replace />;
  }

  return <Outlet />;
}

function NavButton({
  icon,
  label,
  to,
  unreadCount,
  onNavClick,
  collapsed,
}: {
  icon: React.ReactNode;
  label?: string;
  to: string;
  unreadCount?: number;
  onNavClick?: () => void;
  collapsed?: boolean;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `${classes.control} ${isActive ? classes.active : ''} ${collapsed ? classes.collapsed : ''}`
      }
      style={{ textDecoration: 'none' }}
      onClick={onNavClick}
    >
      <Button
        variant="transparent"
        color="black"
        leftSection={collapsed ? null : icon}
        radius={0}
        justify={collapsed ? 'center' : 'flex-start'}
        fullWidth
        px={collapsed ? 0 : undefined}
        pl={0}
        style={{
          color: 'inherit',
          backgroundColor: 'transparent',
          display: 'flex',
          justifyContent: collapsed ? 'center' : 'flex-start',
          alignItems: 'center',
          width: '100%',
        }}
        rightSection={
          !collapsed && unreadCount ? (
            <Badge size="xs" variant="filled" className={classes.notificationBadge}>
              {unreadCount}
            </Badge>
          ) : null
        }
      >
        {collapsed ? icon : label}
      </Button>
    </NavLink>
  );
}

function NavSectionHeader({ label, collapsed }: { label: string; collapsed?: boolean }) {
  if (collapsed) return null;
  return <div className={classes.sectionHeader}>{label}</div>;
}

function NestedNavButton({
  icon,
  label,
  to,
  unreadCount,
  isNested = false,
  onNavClick,
  collapsed,
  iconMargin = FIRST_NESTED_ICON_MARGIN,
}: {
  icon: React.ReactNode;
  label?: string;
  to: string;
  unreadCount?: number;
  isNested?: boolean;
  onNavClick?: () => void;
  collapsed?: boolean;
  iconMargin?: number;
}) {
  const indentedIcon = collapsed ? icon : <span style={{ marginLeft: iconMargin }}>{icon}</span>;
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `${isNested ? classes.nestedControl : classes.control} ${isActive ? classes.active : ''} ${collapsed ? classes.collapsed : ''}`
      }
      style={{ textDecoration: 'none', display: 'block' }}
      onClick={onNavClick}
    >
      <Button
        variant="transparent"
        color="black"
        leftSection={collapsed ? null : indentedIcon}
        radius={0}
        justify={collapsed ? 'center' : 'flex-start'}
        fullWidth
        px={0}
        styles={{
          root: {
            color: 'inherit',
            fontSize: isNested ? '14px' : undefined,
            position: 'relative',
          },
        }}
        rightSection={
          !collapsed && unreadCount ? (
            <Badge size="xs" variant="filled" className={classes.notificationBadge}>
              {unreadCount}
            </Badge>
          ) : null
        }
      >
        {collapsed ? icon : label}
      </Button>
    </NavLink>
  );
}

function CollapsibleNavSection({
  icon,
  label,
  children,
  defaultOpen = true,
  collapsed,
  isNested = false,
  iconMargin = FIRST_NESTED_ICON_MARGIN,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  collapsed?: boolean;
  isNested?: boolean;
  iconMargin?: number;
}) {
  const [isOpened, setIsOpened] = useState(defaultOpen);

  const toggleSection = () => {
    setIsOpened((prev) => !prev);
  };

  const headerClass = isNested ? classes.nestedControl : classes.control;
  const indentedIcon = isNested && !collapsed ? <span style={{ marginLeft: iconMargin }}>{icon}</span> : icon;

  return (
    <>
      <div
        className={`${headerClass} ${collapsed ? classes.collapsed : ''}`}
        style={isNested ? { display: 'block' } : undefined}
        onClick={toggleSection}
      >
        <Button
          variant="transparent"
          color="black"
          radius={0}
          justify={collapsed ? 'center' : 'flex-start'}
          w="100%"
          px={collapsed ? 0 : undefined}
          pl={0}
          styles={{
            root: {
              color: 'inherit',
              backgroundColor: 'transparent',
              display: 'flex',
              justifyContent: collapsed ? 'center' : 'flex-start',
              alignItems: 'center',
              width: '100%',
              fontSize: isNested ? '14px' : undefined,
            },
          }}
          leftSection={collapsed ? null : indentedIcon}
          rightSection={
            collapsed ? undefined : (
              <IconChevronDown size={14} className={classes.sectionChevron} data-open={isOpened} />
            )
          }
        >
          {collapsed ? icon : label}
        </Button>
      </div>

      <Accordion
        value={isOpened ? 'content' : null}
        onChange={toggleSection}
        variant="default"
        chevronPosition="right"
        styles={{
          content: {
            padding: 0,
            paddingLeft: 0,
          },
          control: {
            padding: 0,
          },
          item: {
            border: 'none',
          },
          chevron: {
            display: 'none',
          },
        }}
      >
        <Accordion.Item value="content">
          <Accordion.Control style={{ display: 'none' }} />
          <Accordion.Panel>
            <Stack gap={0} align="stretch">
              {children}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </>
  );
}

function NavButtons({
  onNavClick,
  collapsed,
  unreadCount = 0,
  onToggleCollapse,
}: {
  onNavClick?: () => void;
  collapsed?: boolean;
  unreadCount?: number;
  onToggleCollapse: () => void;
}) {
  const {
    currentUserPermissions: {
      isAdmin,
      hasAdminMembership,
      canAccessPartners,
      canConfigureIvr,
      canViewCiManagerDashboard,
      canViewFinanceOps,
    },
  } = useCurrentUser();
  const isPatientCentralEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_PATIENT_CENTRAL);
  const isIVRConfigurationEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_IVR_CONFIGURATION);
  const isPatientExportEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_PATIENT_EXPORT);
  const isTaskManagementEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_TASK_MANAGEMENT);
  const isCiManagerDashboardEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_CI_MANAGER_DASHBOARD);
  const isFinanceOpsEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_FINANCE_OPS);
  const isDevMode = import.meta.env.MODE !== 'production';
  const isAdminUser = isAdmin || hasAdminMembership;
  // useFeatureFlagEnabled resolves to undefined until the flag loads; coerce so the
  // section predicate takes a real boolean rather than a falsy maybe.
  const canSeeIvrConfig = !!isIVRConfigurationEnabled && canConfigureIvr;
  const canSeeCiManagerDashboard = !!isCiManagerDashboardEnabled && canViewCiManagerDashboard;
  const canSeeFinanceOps = !!isFinanceOpsEnabled && canViewFinanceOps;
  const hasAdminDevItems = hasAdminDevSectionItems({ isDevMode, isAdminUser, canSeeIvrConfig });
  const hasPatientAccessItems = hasPatientAccessSectionItems({ canSeeCiManagerDashboard });
  const hasFinanceItems = hasFinanceSectionItems({ canSeeFinanceOps });

  return (
    <Stack style={{ gap: 0, padding: collapsed ? 'xs' : 'md' }} onClick={(e) => e.stopPropagation()}>
      <Flex
        justify={collapsed ? 'center' : 'space-between'}
        align="center"
        direction={collapsed ? 'column-reverse' : 'row'}
        my={collapsed ? 'xs' : 'md'}
        px={collapsed ? 'xs' : 'md'}
        gap={collapsed ? 'md' : 0}
        mb="md"
      >
        <Image
          src={collapsed ? everselfIcon : everselfLogo}
          alt="Everself Logo"
          w={collapsed ? NAVBAR_WIDTH.collapsed * 0.3 : NAVBAR_WIDTH.expanded * 0.5}
          h="auto"
          mx={collapsed ? 'auto' : 0}
        />
        <UnstyledButton
          component={Link}
          to="/"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: collapsed ? 'xs' : 0,
          }}
        >
          <ActionIcon
            variant="transparent"
            className={classes.collapseButton}
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleCollapse();
            }}
            aria-label="Expand navbar"
          >
            {collapsed ? (
              <IconLayoutSidebarLeftExpand size={18} className={classes.collapseIcon} />
            ) : (
              <IconLayoutSidebarLeftCollapse size={18} className={classes.collapseIcon} />
            )}
          </ActionIcon>
        </UnstyledButton>
      </Flex>

      {/* Notifications section */}
      <CollapsibleNavSection icon={<IconBell size={ICON_SIZES.medium} />} label="Notifications" collapsed={collapsed}>
        <NestedNavButton
          icon={<IconInbox size={ICON_SIZES.medium} />}
          label="Inbox"
          to="/Inbox"
          unreadCount={unreadCount > 0 ? unreadCount : undefined}
          isNested
          onNavClick={onNavClick}
          collapsed={collapsed}
        />
        <NestedNavButton
          icon={<IconArchive size={ICON_SIZES.medium} />}
          label="Archive"
          to="/Archive"
          isNested
          onNavClick={onNavClick}
          collapsed={collapsed}
        />
      </CollapsibleNavSection>

      <NavButton
        icon={<IconCalendar size={ICON_SIZES.medium} />}
        label="Calendar"
        to="/Calendar"
        onNavClick={onNavClick}
        collapsed={collapsed}
      />

      {/* Other navigation items */}
      {NAV_ITEMS({ canAccessPartners })
        .filter((item) => item !== undefined)
        .map((item) => (
          <NavButton
            key={item.to}
            icon={item.icon}
            label={item.label}
            to={item.to}
            onNavClick={onNavClick}
            collapsed={collapsed}
          />
        ))}

      {isPatientCentralEnabled && (
        <CollapsibleNavSection
          icon={<IconLayoutGrid size={ICON_SIZES.medium} />}
          label="Patient Central"
          defaultOpen={false}
          collapsed={collapsed}
        >
          <NestedNavButton
            icon={<IconStethoscope size={ICON_SIZES.medium} />}
            label="Pre-Procedure"
            to="/PreProcedure"
            isNested
            onNavClick={onNavClick}
            collapsed={collapsed}
          />
          <NestedNavButton
            icon={<IconHeartbeat size={ICON_SIZES.medium} />}
            label="Post-Procedure"
            to="/PostProcedure"
            isNested
            onNavClick={onNavClick}
            collapsed={collapsed}
          />
          <NestedNavButton
            icon={<IconCalendarEvent size={ICON_SIZES.medium} />}
            label="Procedure Scheduling"
            to="/ProcedureScheduling"
            isNested
            onNavClick={onNavClick}
            collapsed={collapsed}
          />
        </CollapsibleNavSection>
      )}

      <NavSectionHeader label="Operations" collapsed={collapsed} />

      <NavButton
        icon={<IconCalendarEvent size={ICON_SIZES.medium} />}
        label="On-call schedule"
        to="/on-call-schedule"
        onNavClick={onNavClick}
        collapsed={collapsed}
      />

      {isPatientExportEnabled && (
        <NavButton
          icon={<IconFileSpreadsheet size={ICON_SIZES.medium} />}
          label="Patient export"
          to="/patient-export"
          onNavClick={onNavClick}
          collapsed={collapsed}
        />
      )}

      <CollapsibleNavSection
        icon={<IconMessage size={ICON_SIZES.medium} />}
        label="Communication logs"
        defaultOpen={false}
        collapsed={collapsed}
      >
        <NestedNavButton
          icon={<IconPhone size={ICON_SIZES.medium} />}
          label="Phone logs"
          to="/Calls"
          isNested
          onNavClick={onNavClick}
          collapsed={collapsed}
        />
        <NestedNavButton
          icon={<IconMessage size={ICON_SIZES.medium} />}
          label="SMS logs"
          to="/SMS"
          isNested
          onNavClick={onNavClick}
          collapsed={collapsed}
        />
        {isTaskManagementEnabled && (
          <NestedNavButton
            icon={<IconListCheck size={ICON_SIZES.medium} />}
            label="Task log"
            to="/task-log"
            isNested
            onNavClick={onNavClick}
            collapsed={collapsed}
          />
        )}
      </CollapsibleNavSection>

      {hasPatientAccessItems && <NavSectionHeader label="Patient access" collapsed={collapsed} />}

      {canSeeCiManagerDashboard && (
        <NavButton
          icon={<IconChartBar size={ICON_SIZES.medium} />}
          label="Manager dashboard"
          to="/pac-manager-dashboard"
          onNavClick={onNavClick}
          collapsed={collapsed}
        />
      )}

      {hasFinanceItems && <NavSectionHeader label="Finance" collapsed={collapsed} />}

      {canSeeFinanceOps && (
        <NavButton
          icon={<IconCoin size={ICON_SIZES.medium} />}
          label="Finance ops"
          to="/finance-ops"
          onNavClick={onNavClick}
          collapsed={collapsed}
        />
      )}

      {hasAdminDevItems && <NavSectionHeader label="Admin & dev" collapsed={collapsed} />}

      {/* Development/Staging only - Simulate Patient Chat */}
      {isDevMode && (
        <NavButton
          icon={<IconTestPipe size={ICON_SIZES.medium} />}
          label="Simulate Chat"
          to="/simulate-patient-chat"
          onNavClick={onNavClick}
          collapsed={collapsed}
        />
      )}

      {canSeeIvrConfig && (
        <NavButton
          icon={<IconDialpad size={ICON_SIZES.medium} />}
          label="IVR Configuration"
          to="/ivr-configuration"
          onNavClick={onNavClick}
          collapsed={collapsed}
        />
      )}

      {isAdminUser && (
        <>
          <NavButton
            icon={<IconTestPipe size={ICON_SIZES.medium} />}
            label="Import Healthie Patients"
            to="/import-healthie-patients"
            onNavClick={onNavClick}
            collapsed={collapsed}
          />
          <NavButton
            icon={<IconTestPipe size={ICON_SIZES.medium} />}
            label="Update Patient States"
            to="/update-patient-states"
            onNavClick={onNavClick}
            collapsed={collapsed}
          />
          <NavButton
            icon={<IconStethoscope size={ICON_SIZES.medium} />}
            label="Providers"
            to="/providers"
            onNavClick={onNavClick}
            collapsed={collapsed}
          />
        </>
      )}
    </Stack>
  );
}

function getPatientCentralRoutes() {
  return (
    <>
      <Route path="/PreProcedure" element={<PreProcedurePage />} />
      <Route path="/PostProcedure" element={<PostProcedurePage />} />
      <Route path="/ProcedureScheduling" element={<ProcedureSchedulingPage />} />

      <Route path="/patient-central/pre-procedure" element={<Navigate to="/PreProcedure" replace />} />
      <Route path="/patient-central/engagement" element={<Navigate to="/PostProcedure?tab=engagement" replace />} />
      <Route path="/patient-central/weight-loss" element={<Navigate to="/PostProcedure?tab=weight-loss" replace />} />
      <Route
        path="/patient-central/adverse-events"
        element={<Navigate to="/PostProcedure?tab=adverse-events" replace />}
      />
      <Route
        path="/patient-central/time-blocks"
        element={<Navigate to="/ProcedureScheduling?tab=time-blocks" replace />}
      />
      <Route path="/patient-central/schedule" element={<Navigate to="/ProcedureScheduling?tab=schedule" replace />} />
      <Route path="/patient-central/scheduler" element={<Navigate to="/ProcedureScheduling?tab=scheduler" replace />} />
      <Route
        path="/patient-central/consultations"
        element={<Navigate to="/ProcedureScheduling?tab=consultations" replace />}
      />
    </>
  );
}

function getIVRConfigurationRoutes() {
  return (
    <>
      <Route path="/pac-manager-dashboard" element={<PacManagerDashboardPage />} />
      {/* Old name of the Patient Access dashboard (BAR-2587); keeps bookmarks working. */}
      <Route path="/ci-manager-dashboard" element={<Navigate to="/pac-manager-dashboard" replace />} />
      <Route path="/ivr-configuration" element={<IVRConfigPage />} />
      <Route path="/ivr-configuration/:templateId" element={<IVRTemplateEditorPage />} />
      <Route path="/on-call-schedule" element={<OnCallSchedulePage />} />
    </>
  );
}

/**
 * Renders the nav with its Inbox badge count.
 *
 * The count includes role-assigned clinical tasks, so useNotificationsCount
 * reads the current user's role from CurrentUserContext. App itself RENDERS
 * CurrentUserProvider, so calling the hook in App's own body puts it above
 * the provider and throws on mount — taking down the whole app. Keep the
 * call in this wrapper, which only ever renders inside the provider.
 */
function NavButtonsWithUnreadCount(props: {
  onNavClick?: () => void;
  collapsed?: boolean;
  onToggleCollapse: () => void;
}): ReactElement {
  const unreadCount = useNotificationsCount();
  return <NavButtons {...props} unreadCount={unreadCount} />;
}

export function App(): ReactElement | null {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner;
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [mobileNavbarOpened, { toggle: toggleMobileNavbar }] = useDisclosure();
  const [navbarCollapsed, { toggle: toggleNavbar }] = useDisclosure(false);

  const isPatientCentralEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_PATIENT_CENTRAL);
  const isIVRConfigurationEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_IVR_CONFIGURATION);
  const isPatientExportEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_PATIENT_EXPORT);
  const isFinanceOpsEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_FINANCE_OPS);

  // Track sidebar toggle
  useEffect(() => {
    // Skip tracking on initial mount
    if (navbarCollapsed !== false) {
      trackSidebarToggled(posthog, {
        new_state: navbarCollapsed ? 'collapsed' : 'expanded',
      });
    }
  }, [navbarCollapsed, posthog]);

  // Track push notification clicks for analytics
  useNotificationAnalytics();

  // Detect if we're on mobile (below 'sm' breakpoint)
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Handle collapse button click - on mobile it closes the menu, on desktop it collapses the navbar
  const handleCollapseClick = () => {
    if (isMobile) {
      // On mobile, just close the burger menu
      toggleMobileNavbar();
    } else {
      // On desktop, toggle the navbar collapse state
      toggleNavbar();
    }
  };

  // On app load (and whenever the authenticated practitioner changes), ensure a push subscription exists
  // and is registered on the server. This only runs if the user has already granted permission.
  useEffect(() => {
    if (profile?.id) {
      ensurePushSubscription(medplum, profile.id);
    }
  }, [medplum, profile?.id]);

  // Listen for service worker messages to handle push notification actions that are not strictly phone controls
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      handleCallActionEvent(medplum, navigate, event);
    };
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handler);
    }
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handler);
      }
    };
  }, [navigate, medplum]);

  if (medplum.isLoading()) {
    return <Loading />;
  }

  return (
    <AppShell
      header={{ height: 60, offset: false }}
      navbar={{
        width: !isMobile && navbarCollapsed ? NAVBAR_WIDTH.collapsed : NAVBAR_WIDTH.expanded,
        breakpoint: 'sm',
        collapsed: { mobile: !mobileNavbarOpened },
      }}
      className={`${classes.root} ${location.pathname === '/signin' ? classes.loginPage : ''}`}
    >
      <CurrentUserProvider>
        <FeatureFlagsProvider>
          <TwilioPhoneProvider>
            <AppShell.Header className={classes.header}>
              <Group h="100%" px="md" justify="space-between">
                {/* reserve navbar space */}
                <Box
                  style={{
                    minWidth: !isMobile && navbarCollapsed ? NAVBAR_WIDTH.collapsed * 0.6 : NAVBAR_WIDTH.expanded * 0.9,
                  }}
                />
                <Group>
                  <Burger opened={mobileNavbarOpened} onClick={toggleMobileNavbar} hiddenFrom="sm" size="sm" />
                </Group>
                {profile ? (
                  <>
                    <Group justify="left" style={{ flex: 1 }}>
                      <Box mr="md" w="50%">
                        <GlobalSearch />
                      </Box>
                    </Group>
                    <Group gap="0">
                      <GlobalPhoneControls />
                      <UserMenu profile={profile} />
                    </Group>
                  </>
                ) : null}
              </Group>
            </AppShell.Header>

            {profile ? (
              <AppShell.Navbar className={classes.navbar}>
                <NavButtonsWithUnreadCount
                  onNavClick={toggleMobileNavbar}
                  collapsed={!isMobile && navbarCollapsed}
                  onToggleCollapse={handleCollapseClick}
                />
              </AppShell.Navbar>
            ) : null}

            <AppShell.Main pt={`calc(${rem(60)}`} pl={profile ? undefined : 0}>
              <ErrorBoundary>
                <Suspense fallback={<Loading />}>
                  <Routes>
                    {/* Public Routes */}
                    <Route element={<PublicOnlyRoute />}>
                      <Route path="/signin" element={<SignInPage />} />
                      <Route path="/resetpassword" element={<ResetPasswordPage />} />
                      <Route path="/setpassword/:id/:secret" element={<SetPasswordPage />} />
                    </Route>

                    {/* Protected Routes */}
                    <Route element={<ProtectedRoute />}>
                      <Route path="/" element={<Navigate to="/Inbox" />} />
                      <Route path="/Calendar" element={<SchedulingPage />} />
                      <Route path="/Patient" element={<PatientListPage />} />
                      <Route path="/Patient/:patientId/timeline" element={<PatientTimelinePage />} />
                      <Route path="/Calls" element={<PhoneCallsPage />} />
                      <Route path="/SMS" element={<SmsMessagesPage />} />
                      <Route path="/task-log" element={<TaskLogPage />} />
                      <Route path="/Faxing" element={<FaxingPage />} />
                      <Route path="/Inbox" element={<InboxPage />} />
                      <Route path="/Archive" element={<ArchivePage />} />
                      <Route path="/changepassword" element={<ChangePasswordPage />} />
                      <Route path="/Organizations" element={<PartnersPage />} />
                      <Route path="/Organizations/:organizationId/timeline" element={<PartnerTimelinePage />} />
                      {import.meta.env.MODE !== 'production' && (
                        <Route path="/simulate-patient-chat" element={<SimulatePatientChatPage />} />
                      )}
                      <Route path="/import-healthie-patients" element={<ImportHealthiePatientsPage />} />
                      <Route path="/update-patient-states" element={<UpdatePatientStatesPage />} />
                      <Route path="/providers" element={<ProvidersPage />} />

                      {isPatientExportEnabled && <Route path="/patient-export" element={<PatientExportPage />} />}
                      {isFinanceOpsEnabled && <Route path="/finance-ops" element={<FinanceOpsPage />} />}
                      {isIVRConfigurationEnabled && getIVRConfigurationRoutes()}
                      {isPatientCentralEnabled && getPatientCentralRoutes()}
                    </Route>
                  </Routes>
                </Suspense>
              </ErrorBoundary>
            </AppShell.Main>

            {/* Soft prompt to encourage enabling push notifications */}
            {profile ? <PushNotificationsPrompt /> : null}

            <GlobalCallNotification />
          </TwilioPhoneProvider>
        </FeatureFlagsProvider>
      </CurrentUserProvider>
    </AppShell>
  );
}
